import { useEffect } from "react";
import { Form, Modal, notification, Select, InputNumber, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "store/slices/modalsSlice";
import { getInstructors, selectAssignableInstructors } from "store/slices/instructorsSlice";
import { addFormerToGroup, deleteFormerFromGroup, getGroupById } from "store/slices/groupsSlice";

const { Text } = Typography;

const AddFormerToGroupModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form?.useForm();

  // Selectors
  const { isModalOpen, modalType, modalProps } = useSelector(
    (state) => state?.modals
  );
  const { group, loading } = useSelector((state) => state?.groups);
  const instructors = useSelector(selectAssignableInstructors);

  const open = isModalOpen && modalType === "add_former_to_group";
  const groupId = modalProps?.groupId;

  // Charger les données nécessaires
  useEffect(() => {
    if (open) {
      // Charger les formateurs
      dispatch(getInstructors());

      // Charger les détails du groupe si pas déjà chargés
      if (!group || group?._id !== groupId) {
        dispatch(getGroupById(groupId));
      }
    }
  }, [dispatch, open, groupId, group]);

  // Gérer la soumission du formulaire
  const onSubmit = async (values) => {
    try {
      // Support both single instructor and multiple instructors
      const instructorIds = Array.isArray(values?.formateurIds)
        ? values.formateurIds
        : (values?.formateurId ? [values.formateurId] : []);

      const initialIds = Array.from(currentInstructorIds);
      
      const toAdd = instructorIds.filter(id => !initialIds.includes(id));
      const toRemove = initialIds.filter(id => !instructorIds.includes(id));

      // Add or update each selected instructor
      for (const formateurId of instructorIds) {
        if (!toRemove.includes(formateurId)) {
          await dispatch(
            addFormerToGroup({
              groupId,
              formateurId,
              paymentType: values?.paymentTypes?.[formateurId],
              paymentValue: values?.paymentValues?.[formateurId],
            })
          )?.unwrap();
        }
      }

      // Remove each removed instructor
      for (const formateurId of toRemove) {
        await dispatch(
          deleteFormerFromGroup({
            groupId,
            formateurId,
          })
        )?.unwrap();
      }

      // dispatch le groupe
      if (groupId) {
        dispatch(getGroupById(groupId));
      }

      notification?.success({
        message: t("groups.crud.form.add.former.success"),
        duration: 3,
      });

      onClose();
    } catch (error) {
      notification?.error({
        message: t("groups.crud.form.add.former.error"),
        description: error?.message || t("common.error.generic"),
        duration: 4,
      });
    }
  };

  // Fermer le modal
  const onClose = () => {
    form?.resetFields();
    dispatch(closeModal());
  };

  // Get current instructor IDs from the group (support both formats)
  const getCurrentInstructorIds = () => {
    const ids = new Set();

    const addId = (value) => {
      if (!value) return;
      if (typeof value === "string") {
        ids.add(value);
        return;
      }
      if (value?._id) {
        ids.add(value._id);
      }
    };

    // New format: instructors array
    if (Array.isArray(group?.instructors)) {
      group.instructors.forEach((instructor) => addId(instructor));
    } else if (group?.instructors) {
      addId(group.instructors);
    }

    // Legacy format: single instructor
    addId(group?.instructor);

    // Legacy format: formers array
    if (group?.formers && Array.isArray(group.formers)) {
      group.formers.forEach((former) => {
        addId(former?.formateurId);
      });
    }

    return ids;
  };

  const currentInstructorIds = getCurrentInstructorIds();

  useEffect(() => {
    if (open) {
      const pTypes = {};
      const pValues = {};
      
      const instructorIdsArray = Array.from(currentInstructorIds);

      // Populate from group.paymentData
      if (group?.paymentData && Array.isArray(group.paymentData)) {
        group.paymentData.forEach(p => {
          if (p.instructorId) {
            pTypes[p.instructorId] = p.paymentMode;
            pValues[p.instructorId] = p.paymentValue;
          }
        });
      }
      
      // Default values for existing without payment data
      instructorIdsArray.forEach(id => {
        if (!pTypes[id]) {
          pTypes[id] = "percentage";
          pValues[id] = 40;
        }
      });

      form.setFieldsValue({
        formateurIds: instructorIdsArray,
        paymentTypes: pTypes,
        paymentValues: pValues,
      });
    }
  }, [open, form, group]);

  return (
    <Modal
      title={t("groups.crud.form.manage.instructors.title")}
      open={open}
      onCancel={onClose}
      onOk={() => form?.submit()}
      confirmLoading={loading}
      destroyOnClose
      centered
      width={600}
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          name="formateurIds"
          label={t("groups.crud.form.label.instructors")}
          rules={[
            {
              required: true,
              message: t("groups.crud.form.label.instructors.required"),
            },
          ]}
        >
          <Select
            mode="multiple"
            placeholder={t("groups.crud.form.manage.instructors.placeholder")}
            loading={loading}
            maxTagCount="responsive"
          >
            {instructors?.map((instructor) => (
              <Select.Option key={instructor?._id} value={instructor?._id}>
                {instructor?.fullName}
              </Select.Option>

            ))}
          </Select>
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.formateurIds !== curr.formateurIds}>
          {({ getFieldValue }) => {
            const selectedIds = getFieldValue("formateurIds") || [];
            return selectedIds.map(id => {
              const instructor = instructors?.find(inst => inst._id === id);
              return (
                <Space key={id} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Text style={{ width: 150 }} ellipsis title={instructor?.fullName}>
                    {instructor?.fullName}
                  </Text>
                  
                  <Form.Item
                    name={["paymentTypes", id]}
                    rules={[{ required: true, message: t("groups.crud.form.error.required.paymentMode") }]}
                    style={{ margin: 0, width: 150 }}
                  >
                    <Select
                      placeholder={t("groups.crud.form.placeholder.paymentMode")}
                      options={[
                        { label: t("groups.crud.form.option.paymentMode.fixedPerHour"), value: "fixedPerHour" },
                        { label: t("groups.crud.form.option.paymentMode.percentage"), value: "percentage" },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item noStyle shouldUpdate={(prev, curr) => prev.paymentTypes?.[id] !== curr.paymentTypes?.[id]}>
                    {({ getFieldValue }) => {
                      const pType = getFieldValue(["paymentTypes", id]);
                      if (pType === "percentage") {
                        return (
                          <Form.Item
                            name={["paymentValues", id]}
                            rules={[{ required: true, message: t("groups.crud.form.error.required.paymentValue.percentage") }]}
                            style={{ margin: 0, width: 150 }}
                          >
                            <InputNumber min={0} max={100} precision={2} addonAfter="%" placeholder={t("groups.crud.form.placeholder.paymentValue.percentage")} />
                          </Form.Item>
                        );
                      }
                      return (
                        <Form.Item
                          name={["paymentValues", id]}
                          rules={[{ required: true, message: t("groups.crud.form.error.required.paymentValue.fixedPerHour") }]}
                          style={{ margin: 0, width: 150 }}
                        >
                          <InputNumber style={{ width: "100%" }} min={0} precision={2} placeholder={t("groups.crud.form.placeholder.paymentValue.fixedPerHour")} />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                </Space>
              );
            });
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddFormerToGroupModal;
